import React, { useEffect, useMemo, useRef, useState } from "react";
import useFetch from "../../hooks/fetchMSSQL-hook";
import TagsFilter from "./TagsFilter";
import { NotificationManager } from "react-notifications";

const MAX_EAN_LEN = 30;
const MAX_TEXT_LEN = 255;
const MAX_DESC_LEN = 3000;

export default function ProductForm({ form, handleClose, onAdded, sampleProduct }) {
  const { fetchMssqlApi } = useFetch();

  const isOpen = !!form;
  const isNew = form === "new";

  const [vats, setVATs] = useState([]);
  const [ingredientTypes, setIngredientTypes] = useState([]);
  const [deposits, setDeposits] = useState([]);
  const [tags, setTags] = useState([]);
  const [isTagsOpen, setIsTagsOpen] = useState(false);

  const [uiMsg, setUiMsg] = useState(null);

  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);

  const [data, setData] = useState({
    EAN: "",
    Code: "",
    Name: "",
    VATCode: "",
    IsIngredient: 0,
    IngredientType: "",
    IngredientQuantity: "",
    LastPriceNetto: "",
    IsActive: 1,
    Description: "",
    Image: null,
    DepositId: "",
  });

  const setField = (key) => (e) => { const value = e?.target?.value ?? ""; setUiMsg(null); setData((p) => ({ ...p, [key]: value })); };
  const setEAN = (e) => { setUiMsg(null); const value = (e.target.value || "").replace(/\D/g, ""); setData((p) => ({ ...p, EAN: value })); };

  const toggleIngredient = () => {
    setUiMsg(null);
    setData((p) => {
      const nextIsIng = p.IsIngredient ? 0 : 1;

      return {
        ...p,
        IsIngredient: nextIsIng,
        IngredientQuantity: nextIsIng ? p.IngredientQuantity : "",
        IngredientType: nextIsIng ? p.IngredientType : "",
        DepositId: nextIsIng ? "" : p.DepositId,
      };
    });
  };

  const clearImage = () => {
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImageFile(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const onPickImage = (e) => {
    const file = e.target.files?.[0] || null;

    if (imagePreview) URL.revokeObjectURL(imagePreview);

    if (!file) { setImageFile(null); setImagePreview(null); if (fileInputRef.current) fileInputRef.current.value = ""; return; }

    if (file.type !== "image/png") {
      NotificationManager.error("Tylko PNG", "Błąd", 2500);
      e.target.value = "";
      setImageFile(null);
      setImagePreview(null);
      return;
    }

    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
    e.target.value = "";
  };

  const normalizeApiPayload = (resp) => { const r = resp?.data ?? resp; if (Array.isArray(r)) return r[0]; return r; };
  const parseBackendError = (err) => {
    const payload = err?.response?.data || err?.data || null;
    if (payload?.message) return payload;
    const msg = err?.message || err?.sqlMessage || "";
    if (msg.includes(":")) { const [code, ...rest] = msg.split(":"); return { code: code.trim(), message: rest.join(":").trim() }; }
    return { code: "ERROR", message: "Nie udało się dodać produktu" };
  };

  const errors = useMemo(() => {
    const e = {};

    if (process.env.REACT_APP_LOCAL_DB === "false") {
      if (data.Code.length > MAX_TEXT_LEN) e.Code = `Max ${MAX_TEXT_LEN} znaków`;
      if (!data.VATCode) e.VATCode = "Wybierz VAT";
    } else {
      if (!data.VatId) e.VatId = "Wybierz VAT";
    }

    if (!data.EAN) e.EAN = "EAN jest wymagany";
    else if (!/^\d+$/.test(data.EAN)) e.EAN = "EAN tylko cyfry";
    else if (data.EAN.length > MAX_EAN_LEN) e.EAN = `Max ${MAX_EAN_LEN} znaków`;

    if (!data.Name.trim()) e.Name = "Nazwa jest wymagana";
    else if (data.Name.length > MAX_TEXT_LEN) e.Name = `Max ${MAX_TEXT_LEN} znaków`;

    const priceRaw = String(data.LastPriceNetto ?? "").trim();
    const price = priceRaw === "" ? null : Number(priceRaw);
    if (price !== null && (!Number.isFinite(price) || price < 0 || price > 99999999.99)) e.LastPriceNetto = "Niepoprawna cena";

    if (data.IsIngredient) {
      const q = Number(data.IngredientQuantity);
      if (data.IngredientQuantity === "") e.IngredientQuantity = "Ilość składnika jest wymagana";
      else if (!Number.isFinite(q) || q <= 0 || q > 99999999.99) e.IngredientQuantity = "Niepoprawna ilość";
      if (!data.IngredientType) e.IngredientType = "Wybierz typ składnika";
      else if (!/^[A-Z]$/.test(data.IngredientType)) e.IngredientType = "Niepoprawny typ";
    }

    if (data.Description && data.Description.length > MAX_DESC_LEN) e.Description = `Max ${MAX_DESC_LEN} znaków`;

    return e;
  }, [data]);

  const canSubmit = Object.keys(errors).length === 0;

  const activeTagIds = useMemo(() => tags.flatMap((g) => g.options).filter((o) => o.isActive).map((o) => o.tagId), [tags]);

  useEffect(() => () => { if (imagePreview) URL.revokeObjectURL(imagePreview); }, [imagePreview]);

  useEffect(() => {
    if (!isOpen) return;

    fetchMssqlApi("vats", { method: "POST", hideNotification: true, data: { isActive: true }, showLoader: false }, (res) => setVATs(res || []));
    if (process.env.REACT_APP_LOCAL_DB === "false") {
      fetchMssqlApi("deposits", { method: "POST", hideNotification: true, showLoader: false, data: {isActive: true}}, (res) => setDeposits(res || []));
      fetchMssqlApi("products-tags", { showLoader: false }, (res) => {
        const parsed = (res || []).map((t) => {
          const parts = (t.Name || "").split(" - ");
          return { tagId: t.TagId, category: parts[0] || "Inne", name: parts[1] || parts[0] || "Tag", isActive: false, fullName: t.Name};
        });
        const grouped = parsed.reduce((acc, cur) => { if (!acc[cur.category]) acc[cur.category] = []; acc[cur.category].push({ tagId: cur.tagId, name: cur.name, isActive: cur.isActive, fullName: cur.fullName }); return acc; }, {});
        setTags(Object.keys(grouped).map((cat) => ({ label: cat, options: grouped[cat] })));
      });

      fetchMssqlApi(
        "ingredient-types",
          { method: "POST", hideNotification: true, showLoader: false, data: { isActive: true } },
        (res) => setIngredientTypes(res || [])
      );
    }
    setUiMsg(null);
    clearImage();
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveTags = (productId) => {
    if (!activeTagIds.length) return;
    activeTagIds.forEach((tagId) => fetchMssqlApi("product-tag", { method: "POST", data: { productId, tagId }, hideNotification: true }));
  };

  const submit = (e) => {
    e.preventDefault();
    setUiMsg(null);

    if (!isNew) { setUiMsg({ type: "err", text: "Na razie ten formularz jest tylko do dodawania." }); return; }
    if (!canSubmit) { setUiMsg({ type: "err", text: "Popraw pola zaznaczone na czerwono" }); return; }

    const codeToSend = (data.Code || "").trim() || (data.Name || "").trim();

    const payload = {
      EAN: String(data.EAN),
      Code: codeToSend,
      Name: data.Name.trim(),
      VATCode: data.VATCode,
      VatId: data.VatId,
      IsIngredient: Number(data.IsIngredient),
      IngredientType: data.IsIngredient ? (data.IngredientType || null) : null,
      IngredientQuantity: data.IsIngredient ? Number(data.IngredientQuantity) : null,
      LastPriceNetto: String(data.LastPriceNetto ?? "").trim() === "" ? null : Number(data.LastPriceNetto),
      IsActive: Number(data.IsActive),
      Description: data.Description || "",
      Image: data.Image,
      DepositId: data.IsIngredient ? null : (data.DepositId ? Number(data.DepositId) : null),
    };

    let dataToSend = payload;

    if (imageFile) {
      const fd = new FormData();
      Object.entries(payload).forEach(([k, v]) => { if (v === null || v === undefined) return; fd.append(k, String(v)); });
      fd.append("Image", imageFile);
      dataToSend = fd;
    }

    fetchMssqlApi(
      "product",
      { method: "POST", hideNotification: true, data: dataToSend },
      (resp) => {
        const r = normalizeApiPayload(resp);
        if (Number(r?.ok) === 1 || r?.code === "ADDED") {
          const pid = Number(r?.productId ?? r?.ProductId ?? r?.productID ?? null);
          if (!data.IsIngredient && Number.isFinite(pid)) saveTags(pid);
          NotificationManager.success(r?.message || "Produkt dodany", "Sukces", 2500);
          if (typeof onAdded === "function") onAdded();
          if (typeof handleClose === "function") handleClose();
          return;
        }
        setUiMsg({ type: "err", text: r?.message || "Nie udało się dodać produktu" });
      },
      (err) => {
        const parsed = parseBackendError(err);
        setUiMsg({ type: "err", text: parsed?.message || "Nie udało się dodać produktu" });
      }
    );
  };

  if (!isOpen) return null;

  const selectedTagBadges = tags.flatMap((g) => g.options).filter((o) => o.isActive).map((o) => (
    <button key={o.tagId} type="button" className="btn btn-primary badge badge-pill px-2 p-2 my-1 mr-1 font-weight-bold">{o.fullName}</button>
  ));

  return (
    <div className="modal d-block" tabIndex="-1" role="dialog">
      <div className="modal-dialog modal-dialog-scrollable" role="document">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Dodaj produkt</h5>
            <button type="button" className="close" onClick={handleClose}><span>&times;</span></button>
          </div>

          <div className="modal-body">
            {uiMsg && <div className={`alert ${uiMsg.type === "ok" ? "alert-success" : "alert-danger"}`}>{uiMsg.text}</div>}

            <form onSubmit={submit} id="product-form" autoComplete="off">
              <div className="form-group">
                <label>EAN</label>
                <input className={`form-control ${errors.EAN ? "is-invalid" : ""}`} value={data.EAN} onChange={setEAN} />
                {errors.EAN && <div className="invalid-feedback">{errors.EAN}</div>}
              </div>
              {process.env.REACT_APP_LOCAL_DB === "false" && (
                <div className="form-group">
                  <label>Kod dodatkowy</label>
                  <input className={`form-control ${errors.Code ? "is-invalid" : ""}`} value={data.Code} onChange={setField("Code")} />
                  {errors.Code && <div className="invalid-feedback">{errors.Code}</div>}
                </div>
              )}
              <div className="form-group">
                <label>Nazwa</label>
                <input className={`form-control ${errors.Name ? "is-invalid" : ""}`} value={data.Name} onChange={setField("Name")} />
                {errors.Name && <div className="invalid-feedback">{errors.Name}</div>}
              </div>
              {process.env.REACT_APP_LOCAL_DB === "false" ? (
              <div className="form-group">
                <label>VAT</label>
                <select className={`form-control ${errors.VATCode ? "is-invalid" : ""}`} value={data.VATCode} onChange={setField("VATCode")}>
                  <option value=""></option>
                  {vats.map((v) => <option key={v.Code} value={v.Code}>{v.Name ? `${v.Name}` : `${v.Value}%`} [{v.Code}]</option>)}
                </select>
                {errors.VATCode && <div className="invalid-feedback">{errors.VATCode}</div>}
              </div>
              ) : (
              <div className="form-group">
                <label>VAT</label>
                <select className={`form-control ${errors.VatId ? "is-invalid" : ""}`} value={data.VatId} onChange={setField("VatId")}>
                  <option value=""></option>
                  {vats.map((v) => <option key={v.VatId} value={v.Value}>{v.Name ? `${v.Name}` : `${v.Value}%`}</option>)}
                </select>
                {errors.VatId && <div className="invalid-feedback">{errors.VatId}</div>}
              </div>
              )}
              <div className="form-group">
                <label>Ostatnia cena netto (opcjonalne)</label>
                <input type="number" step="0.01" className={`form-control ${errors.LastPriceNetto ? "is-invalid" : ""}`} value={data.LastPriceNetto} onChange={setField("LastPriceNetto")} />
                {errors.LastPriceNetto && <div className="invalid-feedback">{errors.LastPriceNetto}</div>}
              </div>
              {process.env.REACT_APP_LOCAL_DB === "false" && (
                <>
              <div className="d-flex align-items-center mb-2">
                <input type="checkbox" checked={!!data.IsIngredient} onChange={toggleIngredient} style={{ marginRight: 10 }} />
                <div className="font-weight-bold">Składnik</div>
              </div>
      
              {data.IsIngredient ? (
                <>
                  <div className="form-group">
                    <label>Ilość składnika</label>
                    <input type="number" step="0.01" className={`form-control ${errors.IngredientQuantity ? "is-invalid" : ""}`} value={data.IngredientQuantity} onChange={setField("IngredientQuantity")} />
                    {errors.IngredientQuantity && <div className="invalid-feedback">{errors.IngredientQuantity}</div>}
                  </div>
                  <div className="form-group">
                    <label>Typ składnika</label>
                    <select
                      className={`form-control ${errors.IngredientType ? "is-invalid" : ""}`}
                      value={data.IngredientType}
                      onChange={setField("IngredientType")}
                    >
                      <option value=""></option>
                      {ingredientTypes.map((it) => (
                        <option key={it.Code} value={it.Code}>
                          {it.Name}
                        </option>
                      ))}
                    </select>
                    {errors.IngredientType && <div className="invalid-feedback">{errors.IngredientType}</div>}
                  </div>
                </>
              ) : (
                <>
                  <div className="form-group">
                    <label>Kaucja</label>
                    <select className="form-control" value={data.DepositId} onChange={setField("DepositId")}>
                      <option value="">Wybierz</option>
                      {deposits.map((d) => <option key={d.DepositId} value={d.DepositId}>{d.Name} ({d.Price}zł)</option>)}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Tagi</label>
                    <div className="mb-2">
                      {selectedTagBadges}
                      <button type="button" className="btn btn-outline-primary badge badge-pill px-3 p-2 my-1 mr-1" onClick={() => setIsTagsOpen(true)}><i className="fas fa-plus" /></button>
                    </div>

                    {isTagsOpen && <TagsFilter tags={tags} setTags={setTags} multitag={true} handleClose={() => setIsTagsOpen(false)} />}
                  </div>
                </>
              )}
              </>
              )}
              <div className="form-group">
                <label className="d-block">Zdjęcie (PNG)</label>
                <input ref={fileInputRef} type="file" accept="image/png" style={{ display: "none" }} onChange={onPickImage} />

                <div className="border rounded d-flex align-items-center w-100" style={{ padding: 12, background: "#fff", gap: 12 }}>
                  <div className="border rounded overflow-hidden d-flex align-items-center justify-content-center" style={{ width: 88, height: 88, background: "#f8f9fa", flex: "0 0 88px" }}>
                    {imagePreview ? (
                      <img src={imagePreview} alt="Podgląd" onError={(e) => (e.currentTarget.src = sampleProduct)} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    ) : (
                      <div className="text-muted d-flex flex-column align-items-center justify-content-center" style={{ lineHeight: 1 }}>
                        <i className="far fa-image" style={{ fontSize: 26, marginBottom: 6 }} />
                        <div style={{ fontSize: 11 }}>Brak</div>
                      </div>
                    )}
                  </div>

                  <div className="d-flex flex-column w-100" style={{ minWidth: 0 }}>
                    <div className="d-flex align-items-center w-100" style={{ gap: 10 }}>
                      <div className="w-100" style={{ minWidth: 0 }}>
                        <div className="font-weight-bold" style={{ fontSize: 13, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{imageFile?.name ? imageFile.name : "Nie wybrano pliku"}</div>
                        <div className="text-muted" style={{ fontSize: 12, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>PNG • rekomendowane 512×512</div>
                      </div>

                      <div className="d-flex align-items-center" style={{ gap: 8, flex: "0 0 auto" }}>
                        <button type="button" className="btn btn-sm btn-primary" onClick={() => fileInputRef.current?.click()}><i className="fas fa-plus mr-2" />Dodaj</button>
                        <button type="button" className="btn btn-sm btn-outline-danger" onClick={clearImage} disabled={!imagePreview}><i className="fas fa-trash mr-2" />Usuń</button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label>Opis</label>
                <textarea className={`form-control ${errors.Description ? "is-invalid" : ""}`} rows="4" value={data.Description} onChange={setField("Description")} />
                {errors.Description && <div className="invalid-feedback">{errors.Description}</div>}
              </div>
            </form>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-light" onClick={handleClose}>Anuluj</button>
            <button type="submit" form="product-form" className="btn btn-primary" disabled={!canSubmit}>Dodaj</button>
          </div>
        </div>
      </div>
    </div>
  );
}